'use client'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import Image from 'next/image'
import { Bike, Snowflake, Wrench } from 'lucide-react'
import { getLocalizedSlug } from '@/lib/localized-slugs'

interface CallToActionProps {
    locale?: string
}

export default function CallToAction({ locale = 'en' }: CallToActionProps) {
    const t = useTranslations('CTA')
    const tServices = useTranslations('ServicesCTA')

    // Get localized URLs
    const locationSummerSlug = getLocalizedSlug('location-summer', locale as 'en' | 'fr' | 'nl' | 'es' | 'it')
    const atelierSummerSlug = getLocalizedSlug('atelier-summer', locale as 'en' | 'fr' | 'nl' | 'es' | 'it')
    const locationWinterSlug = getLocalizedSlug('location-winter', locale as 'en' | 'fr' | 'nl' | 'es' | 'it')
    const atelierWinterSlug = getLocalizedSlug('atelier-winter', locale as 'en' | 'fr' | 'nl' | 'es' | 'it')

    return (
        <section className="relative pt-10 md:pt-16 pb-20 md:pb-32 overflow-hidden">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <div className="text-center relative z-10 mb-12">
                    <span className="inline-block rounded-full bg-[url('/leather.jpg')] bg-cover bg-center px-5 py-2 text-sm font-semibold text-black mb-6 border border-[#2a1f10]/50 uppercase tracking-wider">
                        {t('badge')}
                    </span>

                    <h2 className="text-foreground text-4xl sm:text-5xl font-bold lg:text-7xl font-[family-name:var(--font-bebas-neue)] mb-6">
                        {t('title')}
                    </h2>
                </div>

                {/* Two big blocks side by side */}
                <div className="grid md:grid-cols-2 gap-8">
                    {/* Summer Block */}
                    <div className="group relative bg-white rounded-2xl overflow-hidden border border-border hover:border-foreground/20 transition-all duration-300">
                        <div className="relative z-10 flex h-full">
                            {/* Thumbnail */}
                            <div className="relative w-32 md:w-40 flex-shrink-0 self-stretch">
                                <Image
                                    src="/7-laux-mtb-emtb-vtt-rental-location.png"
                                    alt=""
                                    fill
                                    sizes="(max-width: 768px) 128px, 160px"
                                    className="object-cover object-top"
                                />
                            </div>

                            <div className="flex-1 p-6">
                                <h3 className="text-2xl sm:text-4xl leading-none font-bold font-[family-name:var(--font-bebas-neue)] text-foreground mb-3">
                                    {tServices('summer.rental.heading')}
                                </h3>

                                <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
                                    {tServices('summer.rental.description')}
                                </p>

                                <div className="flex flex-col sm:flex-row gap-2">
                                    <Link
                                        href={`/${locale}/${locationSummerSlug}`}
                                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white rounded-lg font-bold hover:opacity-90 transition-all duration-300 hover:scale-105 text-sm"
                                    >
                                        <Bike className="w-3.5 h-3.5" />
                                        {tServices('summer.rentalButton')}
                                    </Link>
                                    <Link
                                        href={`/${locale}/${atelierSummerSlug}`}
                                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white rounded-lg font-bold hover:opacity-90 transition-all duration-300 hover:scale-105 text-sm"
                                    >
                                        <Wrench className="w-3.5 h-3.5" />
                                        {tServices('summer.workshopButton')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Winter Block */}
                    <div className="group relative bg-white rounded-2xl overflow-hidden border border-border hover:border-foreground/20 transition-all duration-300">
                        <div className="relative z-10 flex h-full">
                            {/* Thumbnail */}
                            <div className="relative w-32 md:w-40 flex-shrink-0 self-stretch">
                                <Image
                                    src="/7-laux-ski-snowboard-rental-location-snowpark-ressort.png"
                                    alt=""
                                    fill
                                    sizes="(max-width: 768px) 128px, 160px"
                                    className="object-cover object-top"
                                />
                            </div>

                            <div className="flex-1 p-6">
                                <h3 className="text-2xl sm:text-4xl leading-none font-bold font-[family-name:var(--font-bebas-neue)] text-foreground mb-3">
                                    {tServices('winter.rental.heading')}
                                </h3>

                                <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
                                    {tServices('winter.rental.description')}
                                </p>

                                <div className="flex flex-col sm:flex-row gap-2">
                                    <Link
                                        href={`/${locale}/${locationWinterSlug}`}
                                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white rounded-lg font-bold hover:opacity-90 transition-all duration-300 hover:scale-105 text-sm"
                                    >
                                        <Snowflake className="w-3.5 h-3.5" />
                                        {tServices('winter.rentalButton')}
                                    </Link>
                                    <Link
                                        href={`/${locale}/${atelierWinterSlug}`}
                                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white rounded-lg font-bold hover:opacity-90 transition-all duration-300 hover:scale-105 text-sm"
                                    >
                                        <Wrench className="w-3.5 h-3.5" />
                                        {tServices('winter.workshopButton')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
